import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Security,
  TrendingUp,
  Warning,
  CheckCircle,
  Shield,
  Build,
  ShieldOutlined,
  BarChart,
  AttachMoney,
} from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrueCheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupIcon from "@mui/icons-material/Group";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import { SettingsIcon, Target } from "lucide-react";

const tooltipTexts = {
  "Overall Security Posture":
    "Represents the percentage of the application’s security resilience based on identified static code vulnerabilities. A lower value indicates stronger code quality, reduced vulnerabilities, and better adherence to secure coding practices.",
  "True Positive Volume Analysis":
    "Represents the total number of confirmed security vulnerabilities identified during testing. Higher counts indicate greater security exposure requiring immediate attention.",
  "Severity Breakdown":
    "Categorizes vulnerabilities by their potential impact level. Critical and High severity issues require immediate remediation to prevent security breaches.",
  "Vulnerability Density":
    "Measures the concentration of vulnerabilities per lines of code. Higher density indicates areas requiring focused security attention and code review.",
  "Return on Investment":
    "Demonstrates the financial value of security testing by comparing prevention costs to potential breach costs. Higher ROI indicates effective security investment.",
  "Compliance Gap Mapping":
    "Maps identified vulnerabilities to industry standards like OWASP Top 10. Shows compliance status and areas requiring attention for regulatory requirements.",
  "Risk Rating Overview":
    "Calculates the overall risk exposure based on vulnerability severity and exploitability. Higher scores indicate elevated threat levels requiring prioritized remediation.",
  "Business Impact Score":
    "Indicates how vulnerabilities can disrupt business operations or services. Lower percentages suggest minimal risk to business continuity.",
  "Technical Impact Score":
    "Measures the potential technical damage from successful exploits including system compromise, data loss, and service disruption. Higher scores indicate severe technical risks.",
  "Age of Vulnerability":
    "Indicates the average time that identified vulnerabilities have existed in the codebase since detection. A lower value signifies faster resolution and more proactive vulnerability management.",
  "Strategic Recommendations Summary":
    "Provides actionable recommendations based on security analysis findings. Includes immediate actions and long-term strategic improvements for enhanced security posture.",
  "DevSecOps Integration & Maturity":
    "Represents the extent to which security checks are embedded within the development lifecycle through automated SAST scans and secure coding practices. A higher value indicates stronger security culture, automation, and shift-left adoption.",
};

const getSecurityPostureDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Posture";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Posture";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Posture";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Posture";
  } else {
    return "Unknown";
  }
};

// Function to get dynamic description based on business impact score
const getBusinessImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  } else {
    return "Unknown Impact";
  }
};

// Helper to turn a technical-impact score into a label
const getTechnicalImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  }
  return "Unknown Impact";
};

// Function to get dynamic description based on risk weighted score
const getRiskWeightedDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Risk";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Risk";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Risk";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Risk";
  } else {
    return "Unknown Risk";
  }
};

const typographyStyles = {
  title: { fontSize: "23px", fontWeight: 500 },
  subtitle: { fontSize: "18px", fontWeight: 600 },
  body: { fontSize: "18px", fontWeight: 400 },
  caption: { fontSize: "16px", fontWeight: 400 },
};

function InsightsRecommendations({
  run1Metrics,
  run2Metrics,
  insightsAndRecs,
}) {
  console.log("Insights and Recommendations:", insightsAndRecs);
  const run1 = run1Metrics || {};
  const run2 = run2Metrics || {};
  const insights = insightsAndRecs || [];

  const totalVulns = Object.values(
    run1?.vulnerabilityDistribution || {}
  ).reduce((sum, count) => sum + (count || 0), 0);

  const severityPercentages = {
    Critical:
      totalVulns > 0
        ? (
            ((run1?.vulnerabilityDistribution?.Critical || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
    High:
      totalVulns > 0
        ? (
            ((run1?.vulnerabilityDistribution?.High || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
    Medium:
      totalVulns > 0
        ? (
            ((run1?.vulnerabilityDistribution?.Medium || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
    Low:
      totalVulns > 0
        ? (
            ((run1?.vulnerabilityDistribution?.Low || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
  };

  const highestSeverityData = Object.entries(severityPercentages).reduce(
    (max, [name, value]) => {
      const numValue = parseFloat(value);
      return numValue > max.percentage
        ? { name, percentage: numValue, displayValue: value }
        : max;
    },
    { name: "", percentage: 0, displayValue: "0" }
  );

  const run2SeverityPercentages = {
    Critical:
      totalVulns > 0
        ? (
            ((run2?.openVulnerabilityDistribution?.Critical || 0) /
              totalVulns) *
            100
          ).toFixed(1)
        : 0,
    High:
      totalVulns > 0
        ? (
            ((run2?.openVulnerabilityDistribution?.High || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
    Medium:
      totalVulns > 0
        ? (
            ((run2?.openVulnerabilityDistribution?.Medium || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
    Low:
      totalVulns > 0
        ? (
            ((run2?.openVulnerabilityDistribution?.Low || 0) / totalVulns) *
            100
          ).toFixed(1)
        : 0,
  };

  const run2HighestSeverityData = Object.entries(
    run2SeverityPercentages
  ).reduce(
    (max, [name, value]) => {
      const numValue = parseFloat(value);
      return numValue > max.percentage
        ? { name, percentage: numValue, displayValue: value }
        : max;
    },
    { name: "", percentage: 0, displayValue: "0" }
  );
  // Helper function to get data for a specific section dynamically
  const getSectionData = (sectionName) => {
    const section = insights.find((item) =>
      item.section?.toLowerCase().includes(sectionName.toLowerCase())
    );
    return section?.data || null;
  };

  // Extract section-specific data dynamically
  const overallPostureData = getSectionData(
    "Overall Security Posture Analysis"
  );
  const truePositiveData = getSectionData("True Positive Volume Analysis");
  const riskCoverageAnalysis = getSectionData(
    "Risk Coverage Reduction Analysis"
  );
  const severityBreakdownData = getSectionData("Severity Breakdown & Fix Rate");
  const vulnerabilityDensityData = getSectionData("Vulnerability Density");
  const ROIData = getSectionData("Return on Investment (ROI) Assessment");
  const complianceGAPData = getSectionData("Compliance Gap Mapping");
  // const riskRatingAnalysis = getSectionData("Average Risk Rating Analysis");
  const vulnAgeAnalysis = getSectionData(
    "Vulnerability Age & Remediation Time"
  );
  const businessImpactAnalysis = getSectionData("Business Impact Analysis");
  const technicalImpactAnalysis = getSectionData("Technical Impact Analysis");
  const StrategicRecsAnalysis = getSectionData(
    "Strategic Recommendations Summary"
  );
  const devSecOpsAnalysis = getSectionData("DevSecOps Integration & Maturity");

  const getComplianceStatus = () => {
    const {
      Critical = 0,
      High = 0,
      Medium = 0,
    } = run1.vulnerabilityDistribution;

    if (Critical >= 1 || High >= 1 || Medium >= 1) return "Doesn't Meet";
    return "Meets";
  };

  const status = getComplianceStatus();
  const description =
    status === "Meets"
      ? "All Standards Met"
      : status === "Partial"
      ? "Partially Meets Standards"
      : "Standards Not Met";

  const run1Compliance = {
    title: "OWASP Top 10 Standards",
    chart: (
      <Grid container spacing={1} mb={2}>
        <Grid item xs={12}>
          {status}
        </Grid>
      </Grid>
    ),
    description: description,
  };

  const getrun2ComplianceStatus = () => {
    const {
      Critical = 0,
      High = 0,
      Medium = 0,
    } = run2.openVulnerabilityDistribution;

    if (Critical >= 1 || High >= 1 || Medium >= 1) return "Doesn't Meet";
    return "Meets";
  };

  const run2Status = getrun2ComplianceStatus();
  const run2Description =
    run2Status === "Meets"
      ? "All Standards Met"
      : run2Status === "Partial"
      ? "Partially Meets Standards"
      : "Standards Not Met";

  const run2Compliance = {
    title: "OWASP Top 10 Standards",
    chart: (
      <Grid container spacing={1} mb={2}>
        <Grid item xs={12}>
          {run2Status}
        </Grid>
      </Grid>
    ),
    description: run2Description,
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#FFFFFF",
        minHeight: "100vh",
      }}
    >
      {/* Overall Security Posture */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <ShieldOutlined sx={{ fontSize: 24 }} />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Overall Security Posture Analysis
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Overall Security Posture"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#2563eb" }}
              >
                {run2.securityPosture}
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                {getSecurityPostureDescription(run2.securityPosture)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                Security Posture
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
            mt: 3,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {overallPostureData?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {overallPostureData.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}

          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {overallPostureData?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {overallPostureData.root_cause_analysis.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
            mt: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {overallPostureData?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {overallPostureData.tangible_actions.map((actionItem, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {actionItem.action}
                  </Typography>
                  {/* <Chip
                    label={actionItem.responsible_role}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  /> */}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* True Positive Volume Analysis */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <BarChart color="primary" />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              True Positive Volume Analysis
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["True Positive Volume Analysis"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#ff0000" }}
              >
                {run1.vulnerabilityCount}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Run 1 True Positives
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#00b0f0" }}
              >
                {run2.vulnerabilitiesCount}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Run 2 True Positives
              </Typography>
            </Box>
          </Grid>
          {/* <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#39b549" }}
              >
                {truePositiveData.metrics_comparison?.change_percent}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                TP count reduced
              </Typography>
            </Box>
          </Grid> */}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {truePositiveData?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {truePositiveData.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}

          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {truePositiveData?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {truePositiveData.root_cause_analysis.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {truePositiveData?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {truePositiveData.tangible_actions.map((actionItem, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {actionItem.action}
                  </Typography>
                  {/* <Chip
                    label={actionItem.responsible_role}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  /> */}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* Risk Coverage Reduction */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <BarChartIcon
              sx={{
                color: "#dc2626",
                fontSize: "1.25rem",
                marginRight: "0.5rem",
              }}
            />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Risk Rating Overview
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Risk Rating Overview"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#ff0000" }}
              >
                {run1.overallRiskRating.toFixed(1)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                {getRiskWeightedDescription(run1.overallRiskRating)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                Run 1 Risk Rating
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#00b0f0" }}
              >
                {run2.overallRiskRating.toFixed(1)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                {getRiskWeightedDescription(run2.overallRiskRating)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                Run 2 Risk Rating
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#39b549" }}
              >
                {riskCoverageAnalysis?.metrics_comparison?.change_percent
                  ? riskCoverageAnalysis.metrics_comparison.change_percent
                  : "N/A"}
                %
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                {getRiskWeightedDescription(run1.overallRiskRating)}
                <span style={{ margin: "0 8px", color: "#666" }}>→</span>
                {getRiskWeightedDescription(run2.overallRiskRating)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                decreased
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {riskCoverageAnalysis?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {riskCoverageAnalysis.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {riskCoverageAnalysis?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {riskCoverageAnalysis.root_cause_analysis.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {riskCoverageAnalysis?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {riskCoverageAnalysis.tangible_actions.map(
                (actionItem, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {actionItem.action}
                    </Typography>
                  </Stack>
                )
              )}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* Severity Breakdown Analysis */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Severity Breakdown
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Severity Breakdown"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mt: 2, mb: 2, display: "flex" }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                <strong>Run1 Dominant Severity:</strong>{" "}
                <Box
                  component="span"
                  sx={{ color: "error.main", fontWeight: 500 }}
                >
                  {highestSeverityData.name} (
                  {highestSeverityData.percentage.toFixed(1)}%)
                </Box>
              </Typography>
            </Box>
            <Stack spacing={1.5}>
              {[
                {
                  label: `Critical (${severityPercentages.Critical}%)`,
                  value: `${
                    run1.vulnerabilityDistribution?.Critical || 0
                  } vulnerabilities`,
                  color: "#dc2626",
                  percentage: severityPercentages.Critical,
                },
                {
                  label: `High (${severityPercentages.High}%)`,
                  value: `${
                    run1.vulnerabilityDistribution?.High || 0
                  } vulnerabilities`,
                  color: "#ea580c",
                  percentage: severityPercentages.High,
                },
                {
                  label: `Medium (${severityPercentages.Medium}%)`,
                  value: `${
                    run1.vulnerabilityDistribution?.Medium || 0
                  } vulnerabilities`,
                  color: "#d97706",
                  percentage: severityPercentages.Medium,
                },
                {
                  label: `Low (${severityPercentages.Low}%)`,
                  value: `${
                    run1.vulnerabilityDistribution?.Low || 0
                  } vulnerabilities`,
                  color: "#16a34a",
                  percentage: severityPercentages.Low,
                },
              ].map((item, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: "grey.50",
                    border: "2px solid",
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          bgcolor: item.color,
                        }}
                      />
                      <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                        {item.label}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 500,
                          color: item.color,
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ mt: 2, mb: 2, display: "flex" }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                <strong>Run2 Dominant Severity:</strong>{" "}
                <Box
                  component="span"
                  sx={{ color: "error.main", fontWeight: 500 }}
                >
                  {run2HighestSeverityData.name} (
                  {run2HighestSeverityData.percentage.toFixed(1)}%)
                </Box>
              </Typography>
            </Box>
            <Stack spacing={1.5}>
              {[
                {
                  label: `Critical (${run2SeverityPercentages.Critical}%)`,
                  value: `${
                    run2.openVulnerabilityDistributio?.Critical || 0
                  } vulnerabilities`,
                  color: "#dc2626",
                  percentage: run2SeverityPercentages.Critical,
                },
                {
                  label: `High (${run2SeverityPercentages.High}%)`,
                  value: `${
                    run2.openVulnerabilityDistributio?.High || 0
                  } vulnerabilities`,
                  color: "#ea580c",
                  percentage: run2SeverityPercentages.High,
                },
                {
                  label: `Medium (${run2SeverityPercentages.Medium}%)`,
                  value: `${
                    run2.openVulnerabilityDistributio?.Medium || 0
                  } vulnerabilities`,
                  color: "#d97706",
                  percentage: run2SeverityPercentages.Medium,
                },
                {
                  label: `Low (${run2SeverityPercentages.Low}%)`,
                  value: `${
                    run2.openVulnerabilityDistributio?.Low || 0
                  } vulnerabilities`,
                  color: "#16a34a",
                  percentage: run2SeverityPercentages.Low,
                },
              ].map((item, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: "grey.50",
                    border: "2px solid",
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          bgcolor: item.color,
                        }}
                      />
                      <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                        {item.label}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 500,
                          color: item.color,
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Grid>
        </Grid>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Box sx={{ p: 1 }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
              <strong>Insights:</strong>
            </Typography>
            {severityBreakdownData?.key_insights?.length > 0 ? (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {severityBreakdownData.key_insights.map((cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography
                sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
                color="text.secondary"
              >
                No insights available
              </Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {severityBreakdownData?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {severityBreakdownData.root_cause_analysis.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {severityBreakdownData?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {severityBreakdownData.tangible_actions.map(
                (actionItem, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {actionItem.action}
                    </Typography>
                    {/* <Chip
                      label={actionItem.responsible_role}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    /> */}
                  </Stack>
                )
              )}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* Vulnerability Density */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Security color="primary" />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Vulnerability Density
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Vulnerability Density"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#2563eb" }}
              >
                {run1.vulnerabilityDensity}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Run1 Density
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 400, mt: 1 }}>
                Vulnerabilities per KLOC
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#2563eb" }}
              >
                {run2.vulnerabilityDensity}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Run2 Density
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 400, mt: 1 }}>
                Vulnerabilities per KLOC
              </Typography>
            </Box>
          </Grid>
          {/* <Grid item xs={12}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#2563eb" }}
              >
                {run2.securityPosture}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Security Posture
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 400, mt: 1 }}>
                (1-10 scale)
              </Typography>
            </Box>
          </Grid> */}
        </Grid>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
            mt: 3,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {vulnerabilityDensityData?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {vulnerabilityDensityData.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}

          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {vulnerabilityDensityData?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {vulnerabilityDensityData.root_cause_analysis.map(
                (cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                )
              )}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
            mt: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {vulnerabilityDensityData?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {vulnerabilityDensityData.tangible_actions.map(
                (actionItem, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {actionItem.action}
                    </Typography>
                  </Stack>
                )
              )}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* Return on Investment Analysis */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney color="success" />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Return on Investment Analysis
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Return on Investment"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#dc2626" }}
              >
                {run1.ROI}%
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Run 1
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#16a34a" }}
              >
                {run2.ROI}%
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Run 2
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#dc2626" }}
              >
                {ROIData?.metrics_comparison?.change_percent
                  ? ROIData.metrics_comparison?.change_percent
                  : "N/A"}
                %
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Significant Regression
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {ROIData?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {ROIData.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {ROIData?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {ROIData.root_cause_analysis.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {ROIData?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {ROIData.tangible_actions.map((actionItem, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {actionItem.action}
                  </Typography>
                  {/* <Chip
                    label={actionItem.responsible_role}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  /> */}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* Compliance GAP */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon
              sx={{
                color: "#16a34a",
                fontSize: "1.25rem",
                marginRight: "0.5rem",
              }}
            />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Compliance Gap Mapping
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Compliance Gap Mapping"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography sx={{ fontSize: "18px", fontWeight: 500, mb: 2 }}>
          OWASP Alignment
        </Typography>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                display: "flex",
                textAlign: "center",
                justifyContent: "center",
                border: "1px solid #dc2626",
                backgroundColor: "#fef2f2",
                height: "25px",
                borderRadius: "8px",
              }}
            >
              <Typography
                sx={{ fontSize: "18px", fontWeight: 600, color: "#dc2626" }}
              >
                Run1 :{" "}
                {run1Compliance?.description || "No description available"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "center",
                textAlign: "center",
                border: "1px solid #dc2626",
                height: "25px",
                backgroundColor: "#fef2f2",
                borderRadius: "8px",
              }}
            >
              <Typography
                sx={{ fontSize: "18px", fontWeight: 600, color: "#dc2626" }}
              >
                Run2:{" "}
                {run2Compliance?.description || "No description available"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Typography sx={{ fontSize: "18px", fontWeight: 500, mb: 2 }}>
          OWASP Top 10 Mapping:
        </Typography>

        <Grid container spacing={3}>
          {/* Run1 Section */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}
            >
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Run1
              </Typography>
              {(() => {
                // Check if OWASPMetric exists and is an array
                if (!run1?.OWASPMetrics || !Array.isArray(run1.OWASPMetrics)) {
                  return (
                    <Typography
                      sx={{ fontSize: "14px", color: "text.secondary" }}
                    >
                      No OWASP data available
                    </Typography>
                  );
                }

                // Group and count OWASP categories
                const owaspCategoryCounts = run1.OWASPMetrics.reduce(
                  (acc, item) => {
                    const category = item.owasp_category;
                    if (acc[category]) {
                      acc[category].count += 1;
                      acc[category].vulnerabilities.push(
                        item.vulnerability_name
                      );
                    } else {
                      acc[category] = {
                        count: 1,
                        vulnerabilities: [item.vulnerability_name],
                      };
                    }
                    return acc;
                  },
                  {}
                );

                // Convert to array and sort by count (descending)
                return Object.entries(owaspCategoryCounts)
                  .sort(([, a], [, b]) => b.count - a.count)
                  .map(([category, data], index) => (
                    <Stack
                      key={index}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                        gap: { xs: 1, sm: 2 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: "14px", sm: "15px" },
                          fontWeight: 400,
                          color: "#CF5006",
                          flex: 1,
                          minWidth: { xs: "100%", sm: "auto" },
                          wordBreak: "break-word",
                        }}
                      >
                        • {category}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 500,
                          color: "#CF5006",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          minWidth: "fit-content",
                        }}
                      >
                        {data.count}
                      </Typography>
                    </Stack>
                  ));
              })()}
            </Box>
          </Grid>

          {/* Run2 Section - Fixed */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}
            >
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Run2
              </Typography>
              {(() => {
                // Check if owaspResponse exists and is an array
                if (
                  !run2?.owaspResponse ||
                  !Array.isArray(run2.owaspResponse)
                ) {
                  return (
                    <Typography
                      sx={{ fontSize: "14px", color: "text.secondary" }}
                    >
                      No OWASP data available
                    </Typography>
                  );
                }

                // Group and count OWASP categories
                const owaspCategoryCounts = run2.owaspResponse.reduce(
                  (acc, item) => {
                    const category = item.owasp_category;
                    if (acc[category]) {
                      acc[category].count += 1;
                      acc[category].vulnerabilities.push(
                        item.vulnerability_name
                      );
                    } else {
                      acc[category] = {
                        count: 1,
                        vulnerabilities: [item.vulnerability_name],
                      };
                    }
                    return acc;
                  },
                  {}
                );

                // Convert to array and sort by count (descending)
                return Object.entries(owaspCategoryCounts)
                  .sort(([, a], [, b]) => b.count - a.count)
                  .map(([category, data], index) => (
                    <Stack
                      key={index}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                        gap: { xs: 1, sm: 2 },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: "14px", sm: "15px" },
                          fontWeight: 400,
                          color: "#CF5006",
                          flex: 1,
                          minWidth: { xs: "100%", sm: "auto" },
                          wordBreak: "break-word",
                        }}
                      >
                        • {category}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 500,
                          color: "#CF5006",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          minWidth: "fit-content",
                        }}
                      >
                        {data.count}
                      </Typography>
                    </Stack>
                  ));
              })()}
            </Box>
          </Grid>
        </Grid>

        {/* Rest of your component remains the same... */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {complianceGAPData?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {complianceGAPData.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}

          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {complianceGAPData?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {complianceGAPData.root_cause_analysis.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {complianceGAPData?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {complianceGAPData.tangible_actions.map((actionItem, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {actionItem.action}
                  </Typography>
                  <Chip
                    label={actionItem.responsible_role}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

    

      {/* Business Impact Analysis */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <GroupIcon
              sx={{
                color: "#ea580c", // orange-600 equivalent
                fontSize: "1.5rem", // h-5 w-5 equivalent (20px)
                marginRight: "0.5rem", // mr-2 equivalent
              }}
            />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Business Impact Analysis
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Business Impact Score"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#ff0000" }}
              >
                {run1.businessImpactScore.toFixed(1)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                {getBusinessImpactDescription(
                  run1.businessImpactScore.toFixed(1)
                )}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                Run 1
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#00b0f0" }}
              >
                {run2.businessImpactScore.toFixed(1)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                {getBusinessImpactDescription(
                  run2.businessImpactScore.toFixed(1)
                )}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                Run 2
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#39b549" }}
              >
                {businessImpactAnalysis.metrics_comparison?.change_percent
                  ? businessImpactAnalysis.metrics_comparison?.change_percent
                  : "N/A"}
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                {getBusinessImpactDescription(run1.businessImpactScore)}
                <span style={{ margin: "0 8px", color: "#666" }}>→</span>
                {getBusinessImpactDescription(run2.businessImpactScore)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                Change Percentage
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {businessImpactAnalysis?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {businessImpactAnalysis.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {businessImpactAnalysis?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {businessImpactAnalysis.root_cause_analysis.map(
                (cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                )
              )}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {businessImpactAnalysis?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {businessImpactAnalysis.tangible_actions.map(
                (actionItem, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {actionItem.action}
                    </Typography>
                  </Stack>
                )
              )}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* Technical Impact Analysis */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <FlashOnIcon
              sx={{
                color: "#9333ea", // purple-600 equivalent
                fontSize: "1.5rem", // h-5 w-5 equivalent (20px)
                marginRight: "0.5rem", // mr-2 equivalent
              }}
            />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Technical Impact Overview
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Technical Impact Score"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#ff0000" }}
              >
                {run1.technicalImpactScore.toFixed(1)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                {getTechnicalImpactDescription(
                  run1.technicalImpactScore.toFixed(1)
                )}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                Run 1
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#00b0f0" }}
              >
                {run2.technicalImpactScore.toFixed(1)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                {getTechnicalImpactDescription(
                  run2.technicalImpactScore.toFixed(1)
                )}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                Run 2
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#39b549" }}
              >
                {technicalImpactAnalysis.metrics_comparison?.change_percent
                  ? technicalImpactAnalysis.metrics_comparison?.change_percent
                  : "N/A"}
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 400 }}>
                {getTechnicalImpactDescription(run1.technicalImpactScore)}
                <span style={{ margin: "0 8px", color: "#666" }}>→</span>
                {getTechnicalImpactDescription(run2.technicalImpactScore)}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400, mt: 1 }}>
                Change Percentage
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {technicalImpactAnalysis?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {technicalImpactAnalysis.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {technicalImpactAnalysis?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {technicalImpactAnalysis.root_cause_analysis.map(
                (cause, index) => (
                  <Typography
                    key={index}
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {cause}
                  </Typography>
                )
              )}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {technicalImpactAnalysis?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {technicalImpactAnalysis.tangible_actions.map(
                (actionItem, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {index + 1}. {actionItem.action}
                    </Typography>
                    <Chip
                      label={actionItem.responsible_role}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  </Stack>
                )
              )}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* Vulnerability Age & Remediation Time */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <TrueCheckCircleIcon
              sx={{
                color: "#2563eb", // blue-600 equivalent
                fontSize: "1.25rem", // h-5 w-5 equivalent (20px)
                marginRight: "0.5rem", // mr-2 equivalent
              }}
            />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Vulnerability Age & Remediation Time Analysis
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Age of Vulnerability"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#dc2626" }}
              >
                {run2.averageVulnerabilityAge}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Average Age of Vulnerabilities
              </Typography>
            </Box>
          </Grid>
          {/* <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#2563eb" }}
              >
                {run2.averageTimeToRemediation}
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                Average Time to Remediate
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{ fontSize: "42px", fontWeight: 600, color: "#2563eb" }}
              >
                {run2.remediationEffectiveness}%
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 400 }}>
                of age resolved in remediation window
              </Typography>
            </Box>
          </Grid> */}
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {vulnAgeAnalysis?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {vulnAgeAnalysis.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {vulnAgeAnalysis?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {vulnAgeAnalysis.root_cause_analysis.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {vulnAgeAnalysis?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {vulnAgeAnalysis.tangible_actions.map((actionItem, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {actionItem.action}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* Strategic Recommendations Summary */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Target sx={{ color: (theme) => theme.palette.primary.main }} />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              Strategic Recommendations Summary
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["Strategic Recommendations Summary"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography sx={typographyStyles.subtitle} gutterBottom>
              Immediate Actions
            </Typography>
            <List>
              {StrategicRecsAnalysis?.immediate_actions?.map(
                (action, index) => (
                  <ListItem key={`immediate-${index}`}>
                    <ListItemText
                      primary={action.action}
                      secondary={`Responsible: ${action.responsible_role}`}
                      primaryTypographyProps={{
                        sx: { fontSize: "16px", fontWeight: 400 },
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: "14px",
                          color: "text.secondary",
                          mt: 0.5,
                        },
                      }}
                    />
                  </ListItem>
                )
              ) || (
                // Fallback to static content if no data
                <>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Address 2 remaining critical vulnerabilities"
                      primaryTypographyProps={{
                        sx: { fontSize: "16px", fontWeight: 400 },
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Shield color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Deploy WAF rules for high-risk endpoints"
                      primaryTypographyProps={{
                        sx: { fontSize: "16px", fontWeight: 400 },
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Security color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Implement MFA for administrative access"
                      primaryTypographyProps={{
                        sx: { fontSize: "16px", fontWeight: 400 },
                      }}
                    />
                  </ListItem>
                </>
              )}
            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography sx={typographyStyles.subtitle} gutterBottom>
              Long-term Strategy
            </Typography>
            <List>
              {StrategicRecsAnalysis?.long_term_strategy?.map(
                (strategy, index) => (
                  <ListItem key={`longterm-${index}`}>
                    <ListItemText
                      primary={strategy.action}
                      secondary={`Responsible: ${strategy.responsible_role}`}
                      primaryTypographyProps={{
                        sx: { fontSize: "16px", fontWeight: 400 },
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: "14px",
                          color: "text.secondary",
                          mt: 0.5,
                        },
                      }}
                    />
                  </ListItem>
                )
              ) || (
                // Fallback to static content if no data
                <>
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUp color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Integrate DAST into CI/CD pipeline"
                      primaryTypographyProps={{
                        sx: { fontSize: "16px", fontWeight: 400 },
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Expand test coverage with SAST/IAST"
                      primaryTypographyProps={{
                        sx: { fontSize: "16px", fontWeight: 400 },
                      }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Build color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Establish automated remediation workflows"
                      primaryTypographyProps={{
                        sx: { fontSize: "16px", fontWeight: 400 },
                      }}
                    />
                  </ListItem>
                </>
              )}
            </List>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {StrategicRecsAnalysis?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {StrategicRecsAnalysis.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {StrategicRecsAnalysis?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {StrategicRecsAnalysis.root_cause_analysis.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {StrategicRecsAnalysis?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {StrategicRecsAnalysis.tangible_actions.map(
                (actionItem, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
                      {actionItem.responsible_role}:
                    </Typography>
                    <Typography
                      sx={{ fontSize: "16px", fontWeight: 400 }}
                      color="text.secondary"
                    >
                      {actionItem.action}
                    </Typography>
                  </Stack>
                )
              )}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>

      {/* DevSecOps Integration & Maturity */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          border: "1px solid",
          borderColor: "#5391F7",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
          sx={{ position: "relative" }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <SettingsIcon sx={{ color: "primary.main", fontSize: 24 }} />
            <Typography sx={{ fontSize: "24px", fontWeight: 400 }}>
              DevSecOps Integration & Maturity
            </Typography>
          </Stack>

          <Tooltip
            title={tooltipTexts["DevSecOps Integration & Maturity"]}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  maxWidth: 300,
                  fontSize: "14px",
                  lineHeight: 1.4,
                },
              },
            }}
          >
            <IconButton
              size="small"
              sx={{
                padding: "4px",
                color: "#5391F7",
                "&:hover": {
                  backgroundColor: "rgba(83, 145, 247, 0.1)",
                },
              }}
              aria-label="Info"
            >
              <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Insights:</strong>
          </Typography>
          {devSecOpsAnalysis?.key_insights?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {devSecOpsAnalysis.key_insights.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No insights available
            </Typography>
          )}
          <Typography sx={{ fontSize: "18px", fontWeight: 400, mb: 1 }}>
            <strong>Root Cause:</strong>
          </Typography>
          {devSecOpsAnalysis?.root_cause_analysis?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2, mb: 2 }}>
              {devSecOpsAnalysis.root_cause_analysis.map((cause, index) => (
                <Typography
                  key={index}
                  sx={{ fontSize: "16px", fontWeight: 400 }}
                  color="text.secondary"
                >
                  {index + 1}. {cause}
                </Typography>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400, mb: 2 }}
              color="text.secondary"
            >
              No root cause analysis available
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "#787878",
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 400,
              mb: 1,
              color: "#E3112F",
            }}
          >
            <strong>Action items:</strong>
          </Typography>
          {devSecOpsAnalysis?.tangible_actions?.length > 0 ? (
            <Stack spacing={1} sx={{ ml: 2 }}>
              {devSecOpsAnalysis.tangible_actions.map((actionItem, index) => (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: 400 }}
                    color="text.secondary"
                  >
                    {index + 1}. {actionItem.action}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "16px", fontWeight: 400 }}
              color="text.secondary"
            >
              No Action items available
            </Typography>
          )}
        </Paper>
      </Paper>
    </Box>
  );
}

export default InsightsRecommendations;
